import PropTypes from 'prop-types';
import { useCallback, useRef } from 'react';
// form
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useUploadFiles } from 'services/third-party/s3FileUploadServices';
import { isValidUrl } from 'utils/helper';
import RequiredLabel from './RequiredLabel';

//

// ----------------------------------------------------------------------

RHFUploadFile.propTypes = {
  readOnly: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
};

// ----------------------------------------------------------------------

export default function RHFUploadFile({ name, label, readOnly = false, ...other }) {
  const { watch, control, setValue } = useFormContext();

  const inputFile = useRef(null);
  const values = watch();
  const onHandleChange = (value) => {
    setValue(name, value);
  };
  const isFileUploaded = values[name] && isValidUrl(values[name]);
  const fileName = isFileUploaded ? values[name]?.split('/')?.pop() : values[name]?.name;

  if (readOnly && !isFileUploaded) return null;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Stack spacing={1}>
          {fileName && <Typography variant="body2">{label}</Typography>}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #8885',
              borderRight: 'none',
              padding: '0 0 0 12px',
              borderRadius: 1,
              height: 45,
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                width: '65%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {fileName || label}
            </Typography>
            <input
              type="file"
              ref={inputFile}
              onChange={(e) => onHandleChange(e.target.files[0])}
              hidden
            />
            <LoadingButton
              variant="contained"
              component="label"
              sx={{ height: '100%' }}
              onClick={() => {
                if (readOnly && isFileUploaded) window.open(values[name], '_blank');
                else inputFile.current.click();
              }}
            >
              {readOnly && isFileUploaded ? 'View' : 'Upload'}
            </LoadingButton>
          </Box>
          {!!error && (
            <FormHelperText error sx={{ px: 2 }}>
              {error.message}
            </FormHelperText>
          )}
        </Stack>
      )}
    />
  );
}

S3UploadFile.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  multiple: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  width: PropTypes.number,
  fileTypes: PropTypes.array,
  errorMessage: PropTypes.string,
};

export function S3UploadFile({
  name,
  label,
  readOnly = false,
  multiple = false,
  disabled = false,
  width = 300,
  fileTypes = [],
  errorMessage = '',
  required,
  ...other
}) {
  const { control, setValue } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: uploadFiles, isLoading: uploadFilesLoading } = useUploadFiles();

  const inputFile = useRef(null);

  const onHandleChange = (event) => {
    const { files } = event.target;
    handleDrop(files, name, multiple);
  };

  const handleDrop = useCallback(
    async (acceptedFiles, formKey, isMultiple = false) => {
      const files = acceptedFiles;

      const formData = new FormData();
      Object.entries(files)?.forEach(([order, file]) => {
        if (fileTypes?.length > 0 && !fileTypes?.includes(file.type)) {
          enqueueSnackbar(errorMessage, {
            variant: 'error',
          });
        } else {
          formData.append(name, file);
        }
      });
      if (formData.get(name)) {
        await uploadFiles(formData, {
          onSuccess: (data) => {
            const imgUrlValue = isMultiple
              ? data?.data?.data[0]?.imageNames
              : data?.data?.data[0]?.imageNames[0];
            setValue(formKey, imgUrlValue, { shouldValidate: true });
          },
          onError: (error) => {
            setValue(formKey, null, { shouldValidate: true });
          },
        });
      }
    },
    [name, setValue, errorMessage, fileTypes, enqueueSnackbar, uploadFiles]
  );

  const getFileName = (fieldValue) => (multiple ? `${fieldValue?.length} Files` : fieldValue);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState, fieldState: { error } }) => {
        const isFileUploaded = field?.value;
        const fileName = isFileUploaded ? getFileName(field?.value) : '';

        return (
          <Stack
            spacing={1}
            sx={{
              position: 'relative',
              width: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                top: -2,
                left: 10,
                padding: '2px 4px',
                fontSize: '12px',
                color: 'text.disabled',
                backgroundColor: '#fff',
                zIndex: 1,
              }}
            >
              {fileName && <RequiredLabel label={label} required={required} />}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #8885',
                borderRight: 'none',
                padding: '0 0 0 12px',
                borderRadius: 1,
                height: 52,
                width,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  width: '65%',
                }}
              >
                {fileName || <RequiredLabel label={label} required={required} />}
              </Typography>
              <input
                type="file"
                ref={inputFile}
                multiple={multiple}
                onChange={onHandleChange}
                hidden
                readOnly={readOnly}
                disabled={disabled}
              />
              <LoadingButton
                variant="contained"
                component="label"
                loading={uploadFilesLoading}
                disabled={disabled}
                onClick={() => {
                  if (readOnly && isFileUploaded) window.open(field?.value, '_blank');
                  else inputFile.current.click();
                }}
                sx={{
                  minWidth: 80,
                  width: 80,
                  maxWidth: 80,
                  height: '100%',
                }}
              >
                {readOnly && isFileUploaded ? 'View' : 'Upload'}
              </LoadingButton>
            </Box>
            {!!error && (
              <FormHelperText error sx={{ px: 2 }}>
                {error.message}
              </FormHelperText>
            )}
          </Stack>
        );
      }}
    />
  );
}
